import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { legalDocument, type LegalDocumentKind } from '@worki/shared';
import { currentLocale } from '../i18n.js';
import { WorkiLogo } from '../components/WorkiLogo.js';
import { LanguageSwitcher } from '../components/LanguageSwitcher.js';
import '../styles/legal.css';

/**
 * LEGAL-001 — the privacy policy and the terms of use.
 *
 * Public and unauthenticated on purpose. Google Play and the OAuth consent
 * screen both link to the privacy policy from outside the product, and a
 * reviewer who is met by a sign-in form treats the link as broken. The same
 * goes for the customer who follows the link from a quote they were sent.
 *
 * Both documents render from the same component because they are the same
 * shape — a heading, an intro, and a list of sections — and giving each its own
 * page would mean two places to keep the chrome in step.
 */
export function LegalPage({ kind }: { kind: LegalDocumentKind }) {
  const { t } = useTranslation();
  const locale = currentLocale();
  const document_ = legalDocument(kind, locale);

  useEffect(() => {
    const previous = document.title;
    document.title = `${document_.title} · Worki`;
    return () => {
      document.title = previous;
    };
  }, [document_.title]);

  return (
    <div className="public-page legal-page">
      <div className="legal-shell">
        <header className="legal-header">
          <Link to="/welcome" className="legal-brand" aria-label="Worki">
            <WorkiLogo />
          </Link>
          <LanguageSwitcher />
        </header>

        <article className="legal-document">
          <h1>{document_.title}</h1>
          <p className="legal-updated">{document_.updated}</p>
          <p className="legal-intro">{document_.intro}</p>

          {document_.sections.map((section) => (
            <section key={section.heading}>
              <h2>{section.heading}</h2>
              {section.body.map((paragraph) => (
                <p key={paragraph}>{paragraph}</p>
              ))}
            </section>
          ))}

          <p className="legal-contact">
            {/* The address is the one obligation in the document that the
                document itself has to honour, so it is a live mailto rather
                than text a reader has to retype. */}
            <a href={`mailto:${document_.contact}`} dir="ltr">
              {document_.contact}
            </a>
          </p>
        </article>

        <nav className="legal-footer">
          <Link to={kind === 'privacy' ? '/terms' : '/privacy'}>
            {t(kind === 'privacy' ? 'legal.terms.title' : 'legal.privacy.title')}
          </Link>
          <Link to="/welcome">{t('legal.back')}</Link>
        </nav>
      </div>
    </div>
  );
}
